import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../LanguageContext';
import CustomLink from '../components/CustomLink';
import { Youtube, Linkedin } from 'lucide-react';
import T from '../components/T';

// lucide has no TikTok icon; outline mark in the same 2px-stroke style.
const TikTok: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 7.917v4.034a9.948 9.948 0 0 1 -5 -1.951v4.5a6.5 6.5 0 1 1 -8 -6.326v4.326a2.5 2.5 0 1 0 4 2v-11.5h4.083a6.005 6.005 0 0 0 4.917 4.917z" />
  </svg>
);

const STARTUP_CAREER_SOCIALS = [
  { label: 'YouTube', href: 'https://www.youtube.com/@STARTUP.CAREER', Icon: Youtube },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/start-up-career', Icon: Linkedin },
  { label: 'TikTok', href: 'https://www.tiktok.com/@startup.career', Icon: TikTok },
];

// Project heading with the site's signature accent line underneath, sized
// to the title's rendered width (re-measured once web fonts load and on
// resize) rather than a fixed stub.
const ProjectTitle: React.FC<{ k: string; color: string }> = ({ k, color }) => {
  const { language } = useLanguage();
  const ref = useRef<HTMLSpanElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const measure = () => { if (ref.current) setWidth(ref.current.offsetWidth); };
    measure();
    document.fonts?.ready.then(measure).catch(() => {});
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [k, language]);
  return (
    <>
      <h3 className="text-xl text-[var(--color-text-primary)] mb-2">
        <span ref={ref}><T k={k} /></span>
      </h3>
      <div className="h-1 rounded-full mb-3" style={{ backgroundColor: color, width }} />
    </>
  );
};

const CurrentWork: React.FC = () => {
  const [sideProjectsOpen, setSideProjectsOpen] = useState(false);

  return (
    <section id="currently-building" className="current-work py-24 bg-[var(--color-bg-primary)] transition-colors duration-300" style={{ fontFamily: 'Short Stack, cursive', scrollMarginTop: '4rem' }}>
      <div className="container mx-auto px-6">
        <div className="flex flex-col items-center">
          <div className="flex flex-col gap-y-2 w-full sm:w-[80%] md:w-[60%]">
            <h2 className="mb-2 text-2xl md:text-3xl text-center text-[var(--color-text-primary)] transition-colors duration-300" style={{ fontFamily: 'Indie Flower, cursive' }}>
              <T k="homepage.currentWork.heading" />
            </h2>
            <p className="text-base lg:text-lg text-center text-[var(--color-text-secondary)] mb-10 transition-colors duration-300">
              <T k="homepage.currentWork.intro" />
            </p>

            {/* Main focus: startup.career, then Jacobian Systems below it.
                Grappiq is no longer shown here at all (not a current
                focus), and Rewind moves to the small "side projects" area
                further down. */}
            <div>
              <ProjectTitle k="projectsPage.projects.startupCareer.title" color="var(--color-main-accent)" />
              <p className="text-base lg:text-lg text-[var(--color-text-secondary)] mb-2">
                <T k="projectsPage.projects.startupCareer.description" />
              </p>
              {/* Site link and socials on one quiet line (picked in
                  /currently-building-lab): a normal in-prose link for the
                  site, muted icons that take the ink-green accent on hover. */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[var(--color-text-muted)]">
                <CustomLink to="https://startup.career" external={true}><T k="homepage.currentWork.website" /></CustomLink>
                <span className="flex items-center gap-5">
                  {STARTUP_CAREER_SOCIALS.map(({ label, href, Icon }) => (
                    <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={`STARTUP.CAREER on ${label}`} title={label}
                      className="inline-flex p-2 -m-2 text-[var(--color-text-muted)] hover:text-[var(--color-main-accent)] transition-colors duration-200">
                      <Icon size={18} />
                    </a>
                  ))}
                </span>
              </div>
            </div>

            {/* Jacobian Systems, same block shape as startup.career.
                Deliberately unlinked for now (no site to point to yet). */}
            <div className="mt-10">
              <ProjectTitle k="projectsPage.projects.jacobianSystems.title" color="var(--color-alt-accent)" />
              <p className="text-base lg:text-lg text-[var(--color-text-secondary)] mb-2">
                <T k="projectsPage.projects.jacobianSystems.description" />
              </p>
              <p className="text-base lg:text-lg text-[var(--color-text-secondary)]">
                <T k="projectsPage.projects.jacobianSystems.currently" />
              </p>
            </div>

            {/* Side projects — small, secondary, clearly subordinate to
                startup.career above. Real markup is a native
                <details>/<summary> disclosure: no JS needed to render,
                works even with CSS disabled. Left-aligned like the
                project blocks above it. The native triangle marker is
                replaced by the site's own expand-toggle/expand-arrow
                gesture (same "click to expand in place" meaning as About
                Me's tiles) — `open`/`onToggle` mirror the real <details>
                state into `data-expanded` so it can reuse those exact
                selectors. */}
            <details
              className="side-projects expand-toggle mt-10"
              open={sideProjectsOpen}
              onToggle={(e) => setSideProjectsOpen(e.currentTarget.open)}
              data-expanded={sideProjectsOpen || undefined}
            >
              <summary className="side-projects__summary py-2 -my-2 text-sm text-[var(--color-text-muted)] cursor-pointer">
                <span className="expand-arrow"><T k="homepage.currentWork.sideProjectsLabel" /></span>
              </summary>
              <div className="side-projects__body mt-3">
                <h4 className="side-projects__title text-base text-[var(--color-text-primary)] mb-1">
                  <T k="projectsPage.projects.rewind.title" />
                </h4>
                <p className="side-projects__desc text-sm text-[var(--color-text-secondary)] mb-1">
                  <T k="projectsPage.projects.rewind.description" />
                </p>
                {/* Same "Website" link as STARTUP.CAREER above, rather than
                    linking the title itself. */}
                <span className="text-sm">
                  <CustomLink to="https://rewind.camera" external={true}><T k="homepage.currentWork.website" /></CustomLink>
                </span>
              </div>
            </details>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CurrentWork;
